// Inline SVG icons for the m16 app shell. Kept tiny and dependency-free so
// they're SSR-safe and tree-shakeable. All accept standard SVG props.
import type { SVGProps } from 'react';
import { countryConfig } from '../country';

type IconProps = SVGProps<SVGSVGElement>;

export function BrandMark(props: IconProps) {
  if (countryConfig.code === 'kz') {
    // Sun (KZ brand glyph) — a central disc with 12 short triangular rays,
    // matching the KazTender flag-style mark. Single-color like the crescent:
    // no `fill` attribute, so callers keep controlling color via `className`
    // (e.g. `fill-white/90`). Same 24×24 box and props contract.
    return (
      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" {...props}>
        <path d="M7.2 12A4.8 4.8 0 1 0 16.8 12A4.8 4.8 0 1 0 7.2 12ZM11.19 6.26L12 1.5L12.81 6.26ZM14.17 6.62L17.25 2.91L15.57 7.43ZM16.57 8.43L21.09 6.75L17.38 9.83ZM17.74 11.19L22.5 12L17.74 12.81ZM17.38 14.17L21.09 17.25L16.57 15.57ZM15.57 16.57L17.25 21.09L14.17 17.38ZM12.81 17.74L12 22.5L11.19 17.74ZM9.83 17.38L6.75 21.09L8.43 16.57ZM7.43 15.57L2.91 17.25L6.62 14.17ZM6.26 12.81L1.5 12L6.26 11.19ZM6.62 9.83L2.91 6.75L7.43 8.43ZM8.43 7.43L6.75 2.91L9.83 6.62Z" />
      </svg>
    );
  }
  // Crescent moon (m16 brand glyph).
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

export function ListIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
      <path d="M3 4h14v2H3V4zm0 5h14v2H3V9zm0 5h14v2H3v-2z" />
    </svg>
  );
}

/** Density toggle — «Обычный»: roomy detail rows (a thumbnail + two text lines
 *  each), distinct from the dense-lines compact glyph below. */
export function DensityNormalIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
      <path d="M3 4h4.2v4.2H3V4zm5.6.6h8.4v1.4H8.6V4.6zm0 2.4h8.4v1.4H8.6V7zM3 11.8h4.2V16H3v-4.2zm5.6.6h8.4v1.4H8.6v-1.4zm0 2.4h8.4v1.4H8.6v-1.4z" />
    </svg>
  );
}

/** Density toggle — «Компактный»: four thin full-width rows packed tight. */
export function DensityCompactIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
      <path d="M3 4.5h14v1.5H3V4.5zm0 3.5h14v1.5H3V8zm0 3.5h14v1.5H3v-1.5zm0 3.5h14v1.5H3V15z" />
    </svg>
  );
}

export function ChartIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
      <path d="M3 3h2v14H3V3zm12 4h2v10h-2V7zM7 9h2v8H7V9zm4-3h2v11h-2V6z" />
    </svg>
  );
}

/** Star for the ☆ save toggle. `filled` paints it (saved state); otherwise it's
 *  a hollow outline. */
export function StarIcon({ filled, ...props }: IconProps & { filled?: boolean }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={1.6}
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10 2.5l2.35 4.76 5.25.76-3.8 3.7.9 5.23L10 14.98l-4.7 2.47.9-5.23-3.8-3.7 5.25-.76L10 2.5z"
      />
    </svg>
  );
}

export function BookmarkIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} {...props}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5 3h10a1 1 0 0 1 1 1v13l-6-3-6 3V4a1 1 0 0 1 1-1z"
      />
    </svg>
  );
}

export function BellIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} {...props}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10 2a6 6 0 0 1 6 6v3l1.5 2.5H2.5L4 11V8a6 6 0 0 1 6-6z"
      />
      <path strokeLinecap="round" d="M8 16a2 2 0 0 0 4 0" />
    </svg>
  );
}

export function ShieldCheckIcon(props: IconProps) {
  // A shield with a check — "your payment is protected". Used on the /plans
  // reassurance strip, where the card-number objection lives.
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} {...props}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10 2.5 4 4.5v4.2c0 3.6 2.4 6.3 6 7.8 3.6-1.5 6-4.2 6-7.8V4.5L10 2.5z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="m7.5 9.8 1.8 1.8 3.2-3.6" />
    </svg>
  );
}

export function ReceiptIcon(props: IconProps) {
  // A till receipt with a torn edge — the official fiscal check.
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} {...props}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5 2.5h10v15l-2-1.3-2 1.3-1-1.3-1 1.3-2-1.3-2 1.3v-15z"
      />
      <path strokeLinecap="round" d="M7.5 7h5M7.5 10h5" />
    </svg>
  );
}

export function BankIcon(props: IconProps) {
  // "landmark" — the IFI section. A colonnaded institution reads as a bank
  // without implying money, which the layer deliberately never shows.
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.5 8 10 3.5 17.5 8" />
      <path strokeLinecap="round" d="M5.5 9.5v5M8.5 9.5v5M11.5 9.5v5M14.5 9.5v5" />
      <path strokeLinecap="round" d="M3 16.5h14" />
    </svg>
  );
}

export function PackageIcon(props: IconProps) {
  // "box" — the /supply section (cooperation.uz reverse-auction supply
  // requests): goods/services requests, not a budgeted government tender.
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 2 3 5.5v9L10 18l7-3.5v-9L10 2Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 5.5 10 9l7-3.5M10 9v9" />
    </svg>
  );
}

export function BuildingIcon(props: IconProps) {
  // Office tower with a right annex and window dots — the /companies directory
  // (organizations in public procurement). Distinct from BankIcon (an IFI
  // landmark) and UsersIcon (a team of people).
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} {...props}>
      <path strokeLinejoin="round" d="M4 16.5V4.2a.7.7 0 0 1 .7-.7h5.6a.7.7 0 0 1 .7.7v12.3" />
      <path strokeLinejoin="round" d="M11 9h4.3a.7.7 0 0 1 .7.7v6.8" />
      <path
        strokeLinecap="round"
        d="M6 6.5h0M9 6.5h0M6 9.5h0M9 9.5h0M6 12.5h0M9 12.5h0M13.5 12h0"
      />
      <path strokeLinecap="round" d="M2.5 16.5h15" />
    </svg>
  );
}

export function BookIcon(props: IconProps) {
  // Open book — the /guide «Как участвовать» supplier how-to.
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} {...props}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10 5.5C8.7 4.6 6.9 4 5 4H3v11h2c1.9 0 3.7.6 5 1.5 1.3-.9 3.1-1.5 5-1.5h2V4h-2c-1.9 0-3.7.6-5 1.5Z"
      />
      <path strokeLinecap="round" d="M10 5.5v11" />
    </svg>
  );
}

export function UsersIcon(props: IconProps) {
  // "users" — the team-seats nav row (owner + members roster).
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} {...props}>
      <circle cx="7" cy="6.5" r="2.5" />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.5 16v-.5A4.5 4.5 0 0 1 7 11a4.5 4.5 0 0 1 4.5 4.5v.5"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 6.6a2.5 2.5 0 1 1 0 4.9" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 11.1c1.7.35 3 1.72 3 4.4v.5" />
    </svg>
  );
}

export function ChatIcon(props: IconProps) {
  // "message-circle" — replaces a colour emoji so the nav is one visual family.
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} {...props}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M17 9.8a6.6 6.6 0 0 1-9.4 6L3 17l1.2-4.3A6.6 6.6 0 1 1 17 9.8z"
      />
    </svg>
  );
}

export function InfoIcon(props: IconProps) {
  // "info" — replaces a colour emoji so the nav is one visual family.
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} {...props}>
      <circle cx="10" cy="10" r="7.25" />
      <path strokeLinecap="round" d="M10 9.25v4.25" />
      <path strokeLinecap="round" d="M10 6.6h.01" />
    </svg>
  );
}

export function RefreshIcon(props: IconProps) {
  // "refresh-cw" — the catalog auto-update hint; spins only during a refetch.
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}

export function PinIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} {...props}>
      <circle cx="10" cy="8" r="3" strokeLinecap="round" />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10 2C6.7 2 4 4.7 4 8c0 4.5 6 10 6 10s6-5.5 6-10c0-3.3-2.7-6-6-6z"
      />
    </svg>
  );
}

export function TagIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} {...props}>
      <rect x="3" y="3" width="14" height="14" rx="2" />
      <path d="M7 8h6M7 12h4" />
    </svg>
  );
}

/** Awarded/outcome marker — the /results (finished lots with a winner) nav row. */
export function TrophyIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} {...props}>
      <path d="M6 3h8v4a4 4 0 0 1-8 0V3Z" />
      <path d="M6 4H4v1.5A2.5 2.5 0 0 0 6.5 8" />
      <path d="M14 4h2v1.5A2.5 2.5 0 0 1 13.5 8" />
      <path d="M10 11v3M7.5 17h5M8 17c0-1.5.7-3 2-3s2 1.5 2 3" />
    </svg>
  );
}

/** Padlock — the gated-surface glyph. Moved here from `routes/plans.tsx`, where
 *  it was a local `LockGlyph`, so the locked analytics teasers use the SAME lock
 *  rather than the 🔒 emoji: an emoji depends on a font the device may not have
 *  (and renders in whatever colour that font decides), an inline SVG does not. */
export function LockIcon(props: IconProps) {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden {...props}>
      <rect
        x="3.25"
        y="7"
        width="9.5"
        height="6.5"
        rx="1.4"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M5.5 7V5.25a2.5 2.5 0 015 0V7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function ClockIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={2} {...props}>
      <circle cx="10" cy="10" r="7" />
      <path strokeLinecap="round" d="M10 6v4l3 2" />
    </svg>
  );
}

export function SearchIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={2} {...props}>
      <circle cx="8.5" cy="8.5" r="5.5" />
      <path strokeLinecap="round" d="M14.5 14.5l3 3" />
    </svg>
  );
}

export function ChevronDown(props: IconProps) {
  return (
    <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth={2} {...props}>
      <path d="M3 4.5l3 3 3-3" />
    </svg>
  );
}

export function CheckIcon(props: IconProps) {
  return (
    <svg
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M2.5 6.5l2.5 2.5 4.5-5.5" />
    </svg>
  );
}

export function FileCheckIcon(props: IconProps) {
  // "file-check" (lucide) — a document with a tick, for «Подготовить тендерную
  // документацию». Stroke only, so it takes the button's text colour.
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
      <path d="m9 15 2 2 4-4" />
    </svg>
  );
}

export function ArrowRight(props: IconProps) {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2} {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8h10M9 4l4 4-4 4" />
    </svg>
  );
}

export function ChevronLeft(props: IconProps) {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2} {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 4l-4 4 4 4" />
    </svg>
  );
}

export function ChevronRight(props: IconProps) {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2} {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 4l4 4-4 4" />
    </svg>
  );
}

export function SlidersIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6} {...props}>
      <path strokeLinecap="round" d="M2 4.5h9M2 11.5h5" />
      <path strokeLinecap="round" d="M13 4.5h1M9 11.5h5" />
      <circle cx="11.5" cy="4.5" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="7.5" cy="11.5" r="1.6" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function DownloadIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6} {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 2.5v7M5 6.5l3 3 3-3M3 12.5h10" />
    </svg>
  );
}

export function PhoneIcon(props: IconProps) {
  // "phone" (lucide) — replaces the 📞 colour emoji on the call CTA so it
  // renders crisp and inherits the button's text colour (white on `bg-brand`).
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

export function GlobeIcon(props: IconProps) {
  // "globe" (lucide) — a company website link (the winner contact block).
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

export function TelegramIcon(props: IconProps) {
  // The paper plane, as a single filled path so it inherits `currentColor`
  // (brand on the outline button, white on a filled one). Used where we render
  // our OWN Telegram control — telegram.org's widget iframe draws its own logo
  // and is never restyled.
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M21.94 4.6 18.9 19.2c-.23 1.02-.84 1.27-1.7.79l-4.7-3.47-2.27 2.19c-.25.25-.46.46-.95.46l.34-4.8 8.73-7.9c.38-.34-.08-.53-.59-.19L6.98 13.07l-4.64-1.45c-1.01-.32-1.03-1.01.21-1.5l18.14-7c.84-.3 1.58.2 1.25 1.48z" />
    </svg>
  );
}
